/**
 * Conversation Obfuscation
 *
 * Simple reversible obfuscation for local storage.
 * NOT cryptographic security - just prevents casual reading
 * of sensitive system prompts in JSON files.
 *
 * Uses base64 encoding + simple XOR with a static key.
 *
 * Why not encryption?
 * - This is LOCAL storage only, not transmitted over network
 * - User owns their own data on their own machine
 * - We just want to prevent casual inspection of system prompts
 * - True encryption would require key management complexity
 */
// ═══════════════════════════════════════════════════════════════════════════
// CONFIGURATION
// ═══════════════════════════════════════════════════════════════════════════
/**
 * Obfuscation key - used for XOR operation
 * This is NOT a secret - just adds a layer of transformation
 */
const OBFUSCATION_KEY = 'carousel_local_store_v1_2026';
/**
 * Current obfuscation version
 * Increment when changing the algorithm to handle migrations
 */
export const OBFUSCATION_VERSION = 'v1';
// ═══════════════════════════════════════════════════════════════════════════
// CORE FUNCTIONS
// ═══════════════════════════════════════════════════════════════════════════
/**
 * Obfuscate a string for storage
 *
 * Process:
 * 1. UTF-8 encode the string to bytes
 * 2. XOR each byte with the key (repeating)
 * 3. Base64 encode the result
 *
 * @param data - Plain text string to obfuscate
 * @returns Obfuscated base64 string
 *
 * @example
 * const obfuscated = obfuscate('{"messages": [...]}');
 * // Returns: "c2RmYXNkZmFz..." (base64 string)
 */
export function obfuscate(data) {
    // 1. UTF-8 encode
    const encoder = new TextEncoder();
    const encoded = encoder.encode(data);
    // 2. XOR with key (repeating)
    const keyBytes = encoder.encode(OBFUSCATION_KEY);
    const xored = new Uint8Array(encoded.length);
    for (let i = 0; i < encoded.length; i++) {
        xored[i] = encoded[i] ^ keyBytes[i % keyBytes.length];
    }
    // 3. Base64 encode
    // Convert Uint8Array to binary string, then to base64
    let binaryStr = '';
    for (let i = 0; i < xored.length; i++) {
        binaryStr += String.fromCharCode(xored[i]);
    }
    return btoa(binaryStr);
}
/**
 * Deobfuscate a string from storage
 *
 * Process:
 * 1. Base64 decode to bytes
 * 2. XOR each byte with the key (same operation reverses it)
 * 3. UTF-8 decode to string
 *
 * @param data - Obfuscated base64 string
 * @returns Original plain text string
 *
 * @example
 * const original = deobfuscate("c2RmYXNkZmFz...");
 * // Returns: '{"messages": [...]}'
 */
export function deobfuscate(data) {
    // 1. Base64 decode
    const binaryStr = atob(data);
    const xored = new Uint8Array(binaryStr.length);
    for (let i = 0; i < binaryStr.length; i++) {
        xored[i] = binaryStr.charCodeAt(i);
    }
    // 2. XOR with key (same operation reverses it)
    const encoder = new TextEncoder();
    const keyBytes = encoder.encode(OBFUSCATION_KEY);
    const decoded = new Uint8Array(xored.length);
    for (let i = 0; i < xored.length; i++) {
        decoded[i] = xored[i] ^ keyBytes[i % keyBytes.length];
    }
    // 3. UTF-8 decode
    const decoder = new TextDecoder();
    return decoder.decode(decoded);
}
// ═══════════════════════════════════════════════════════════════════════════
// JSON HELPERS
// ═══════════════════════════════════════════════════════════════════════════
/**
 * Obfuscate a JSON object for storage
 *
 * @param data - Object to obfuscate
 * @returns Obfuscated base64 string
 *
 * @example
 * const obfuscated = obfuscateJson({ messages: [...], threads: {...} });
 */
export function obfuscateJson(data) {
    const jsonStr = JSON.stringify(data);
    return obfuscate(jsonStr);
}
/**
 * Deobfuscate a JSON object from storage
 *
 * @param data - Obfuscated base64 string
 * @returns Parsed JSON object
 *
 * @example
 * const conversation = deobfuscateJson<PersistedConversation>(obfuscatedStr);
 */
export function deobfuscateJson(data) {
    const jsonStr = deobfuscate(data);
    return JSON.parse(jsonStr);
}
// ═══════════════════════════════════════════════════════════════════════════
// VALIDATION
// ═══════════════════════════════════════════════════════════════════════════
/**
 * Check if a string appears to be obfuscated (valid base64)
 *
 * @param data - String to check
 * @returns true if it looks like valid base64
 */
export function isObfuscated(data) {
    // Base64 strings only contain [A-Za-z0-9+/=]
    const base64Regex = /^[A-Za-z0-9+/]+=*$/;
    if (!base64Regex.test(data)) {
        return false;
    }
    // Try to decode and verify it produces valid bytes
    try {
        atob(data);
        return true;
    }
    catch {
        return false;
    }
}
/**
 * Verify that obfuscation/deobfuscation works correctly
 * Useful for testing and debugging
 *
 * @param testData - Optional test string (uses default if not provided)
 * @returns true if round-trip succeeds
 */
export function verifyObfuscation(testData) {
    const test = testData ?? '{"test": "Hello, World! 🌍 Special chars: <>&\'""}';
    try {
        const obfuscated = obfuscate(test);
        const deobfuscated = deobfuscate(obfuscated);
        return deobfuscated === test;
    }
    catch {
        return false;
    }
}
